import React from 'react';
import { Button } from '../../ui/Button/Button';
import { Field } from '../ui/Field';
import { SegmentedChoice } from '../ui/SegmentedChoice';
import { Sheet } from '../ui/Sheet';
import { RAIL_ADDRESS_RANGE } from '../../../lib/rcu/catalog/limits';
import { LOAD_TYPE_SPECS } from '../../../lib/rcu/catalog/loadType';
import { RAIL_TYPE_KEYS, RAIL_TYPE_SPECS } from '../../../lib/rcu/catalog/railType';
import type { RailTypeKey } from '../../../lib/rcu/catalog/railType';
import type { DinRail, RcuConfig } from '../../../lib/rcu/model';
import { nextFreeRailAddress } from '../../../lib/rcu/query/lookup';
import { railTypeUsage } from '../../../lib/rcu/query/railUsage';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useCommandFeedback } from '../useCommandFeedback';

export interface RailSheetProps {
  isOpen: boolean;
  /** null creates a rail, otherwise the one being edited. */
  rail: DinRail | null;
  onClose: () => void;
}

const accepts = (type: RailTypeKey): string =>
  RAIL_TYPE_SPECS[type].acceptedLoadTypes.map((key) => LOAD_TYPE_SPECS[key].label).join(', ');

const typeChoices = (config: RcuConfig, editing: DinRail | null) =>
  RAIL_TYPE_KEYS.map((type) => {
    const usage = railTypeUsage(config, type);
    const spec = RAIL_TYPE_SPECS[type];
    return {
      value: type,
      label: spec.label,
      description: `${usage.fitted}/${usage.perTypeLimit} fitted · ${spec.channels} channels · ${accepts(type)}`,
      disabled: editing === null && !usage.canAdd,
    };
  });

/** Add or edit a DIN rail. Its type is fixed once created. */
export const RailSheet: React.FC<RailSheetProps> = ({ isOpen, rail, onClose }) => {
  const config = useRcuConfigStore((state) => state.config);
  const addRail = useRcuConfigStore((state) => state.addRail);
  const updateRail = useRcuConfigStore((state) => state.updateRail);
  const report = useCommandFeedback();

  const [type, setType] = React.useState<RailTypeKey>(
    rail?.type ?? RAIL_TYPE_KEYS[0],
  );
  const [address, setAddress] = React.useState<number>(
    rail?.address ?? nextFreeRailAddress(config, rail?.type ?? RAIL_TYPE_KEYS[0]) ?? 0,
  );
  const [label, setLabel] = React.useState(rail?.label ?? '');

  const taken = (candidate: number): boolean =>
    config.rails.some(
      (fitted) => fitted.type === type && fitted.address === candidate && fitted.id !== rail?.id,
    );

  const chooseType = (next: RailTypeKey): void => {
    setType(next);
    const free = nextFreeRailAddress(config, next);
    if (free !== null) setAddress(free);
  };

  const handleSave = (): void => {
    const saved =
      rail === null
        ? report(addRail({ type, address, label: label.trim() }), 'Rail added.')
        : report(updateRail(rail.id, { address, label: label.trim() }), 'Rail updated.');
    if (saved) onClose();
  };

  return (
    <Sheet
      isOpen={isOpen}
      title={rail === null ? 'Add DIN rail' : 'Edit DIN rail'}
      subtitle={
        rail === null
          ? 'Rails are addressed 0–15, unique per type.'
          : 'Change its address or label. The type is fixed.'
      }
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {rail === null ? 'Add rail' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <SegmentedChoice
          label="Rail type"
          options={typeChoices(config, rail)}
          value={type}
          onChange={chooseType}
          columns={1}
          hint={rail === null ? undefined : 'Delete the rail and add a new one to change its type.'}
        />

        <div>
          <span className="mb-1.5 block text-sm font-medium text-[#333333]">Address</span>
          <div className="grid grid-cols-8 gap-1.5">
            {RAIL_ADDRESS_RANGE.map((candidate) => {
              const isTaken = taken(candidate);
              const isSelected = candidate === address;
              return (
                <button
                  key={candidate}
                  type="button"
                  disabled={isTaken}
                  onClick={() => setAddress(candidate)}
                  className={`h-9 rounded-lg border text-sm font-medium transition-colors ${
                    isSelected
                      ? 'border-[#D4A054] bg-[#D4A054] text-white'
                      : 'border-[#E0E0E0] bg-white text-[#333333] hover:border-[#D4A054]'
                  } disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:text-[#CCCCCC]`}
                >
                  {candidate}
                </button>
              );
            })}
          </div>
          <span className="mt-1.5 block text-xs text-[#666666]">
            Greyed addresses are already used by another {RAIL_TYPE_SPECS[type].shortLabel} rail.
          </span>
        </div>

        <Field
          label="Label"
          value={label}
          onChange={setLabel}
          placeholder="Bedroom ceiling"
          hint="Optional. Helps you match the rail to the drawing."
        />
      </div>
    </Sheet>
  );
};

export default RailSheet;
